import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { thumbnailUrl } from '../utils/media';

const API_URL = process.env.REACT_APP_API_URL || '';

interface Info {
  width: number | null;
  height: number | null;
  sizeBytes: number;
  mimeType: string;
  instagramProblem: { code: string; message: string } | null;
  canFit: boolean;
}

export interface FittedMedia {
  id: string;
  url: string;
  mimeType: string;
  sizeBytes: number;
  width: number;
  height: number;
}

interface Props {
  /** The post's media URLs. Only images uploaded to ShoutVector are checked. */
  urls: string[];
  /** Swap one attached image for its fitted copy. */
  onReplace: (oldUrl: string, fitted: FittedMedia) => void;
  /** Appended to the fix buttons when pressing one does more than swap the image, e.g. " and retry". */
  fixSuffix?: string;
}

/**
 * Each attached image's size, judged against what Instagram's publishing API accepts, with a
 * one-click fix.
 *
 * Instagram refuses images taller than 4:5, and only says so at publish time. On 2026-10-13
 * getting one image to 1080 × 1350 took five uploads and a resize outside the app, because
 * ChatGPT returns 1122 × 1402 whatever size is asked for. The figures come from the server,
 * which applies the same rule when it publishes, so the two cannot disagree.
 *
 * Shown only when Instagram is selected; the other networks take these shapes as they are.
 */
export const InstagramImageCheck: React.FC<Props> = ({ urls, onReplace, fixSuffix = '' }) => {
  const { token } = useAuth();
  const [info, setInfo] = useState<Record<string, Info | null>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState('');

  const key = urls.join('|');
  useEffect(() => {
    let cancelled = false;
    urls.forEach(url => {
      if (url in info) return;
      fetch(`${API_URL}/api/media/info?url=${encodeURIComponent(url)}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(res => (res.ok ? res.json() : null))
        .then(data => { if (!cancelled) setInfo(prev => ({ ...prev, [url]: data })); })
        .catch(() => { if (!cancelled) setInfo(prev => ({ ...prev, [url]: null })); });
    });
    return () => { cancelled = true; };
    // Re-run only when the set of images changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, token]);

  const fit = async (url: string, mode: 'crop' | 'pad') => {
    setBusy(url);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/media/fit`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ url, mode }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || 'Could not resize the image');
        return;
      }
      setInfo(prev => ({
        ...prev,
        [data.url]: { width: data.width, height: data.height, sizeBytes: data.sizeBytes, mimeType: data.mimeType, instagramProblem: null, canFit: false },
      }));
      onReplace(url, data);
    } catch {
      setError('Network error resizing the image');
    } finally {
      setBusy(null);
    }
  };

  const rows = urls
    .map(url => ({ url, info: info[url] }))
    .filter(r => r.info && r.info.width && r.info.mimeType.startsWith('image/'));
  if (!rows.length && !error) return null;

  return (
    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {rows.map(({ url, info: i }) => {
        const size = `${i!.width} × ${i!.height}`;
        const actions = i!.canFit && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
            {busy === url ? (
              <CircularProgress size={18} />
            ) : (
              <>
                <Button size="small" variant={i!.instagramProblem ? 'contained' : 'outlined'} onClick={() => fit(url, 'crop')}>
                  Crop to 1080 × 1350{fixSuffix}
                </Button>
                <Button size="small" onClick={() => fit(url, 'pad')}>
                  Add borders instead{fixSuffix}
                </Button>
              </>
            )}
          </Box>
        );

        return (
          <Box key={url} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <Box
              component="img"
              src={thumbnailUrl(url, 320)}
              alt=""
              sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 0.5, flexShrink: 0, border: 1, borderColor: 'divider' }}
            />
            {i!.instagramProblem ? (
              <Alert severity="error" sx={{ flexGrow: 1, py: 0 }}>
                <strong>Instagram will refuse this image.</strong> {i!.instagramProblem.message}.
                {actions}
              </Alert>
            ) : (
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2">
                  {size}
                  {i!.width === 1080 && i!.height === 1350
                    ? ' — exactly 4:5, ready for Instagram'
                    : i!.canFit
                      ? ' — Instagram takes it, but it is not exactly 1080 × 1350'
                      : ' — fine for Instagram'}
                </Typography>
                {actions}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
